import React, { FunctionComponent } from 'react';
import { ifProp } from 'styled-tools';
import styled, { css, StyledComponent } from 'styled-components';
import { Button, Typography, Colors, Theme, Tooltip, AnalyticsProps } from '@inplayer-org/inplayer-ui';

// utils
import { AssetType } from 'pages/MerchantPages/Assets/assetTypes';
import CommonIcon from 'components/CommonIcon';

// styled components
import { BlueTypography } from 'components/SharedStyledElements';

type AssetTypeCardProps = { isSelected: boolean; isDisabled: boolean; height: string };

const AssetTypeCard: StyledComponent<'div', Theme, AssetTypeCardProps> = styled.div`
  button {
    display: flex;
    width: 100%;
    height: ${({ height }: AssetTypeCardProps) => height};
    border-radius: 10px;
    background: ${Colors.lightGray};
    border-color: ${Colors.gray};
    transition: all 0.3s ease;

    h6 {
      margin-bottom: 0.4rem;
    }

    ${ifProp(
      'isSelected',
      css`
        background: ${Colors.white};
        border: 1px solid ${({ theme }) => theme.palette.primary.main};
        h6,
        p {
          color: ${({ theme }) => theme.palette.primary.main};
        }
      `
    )};

    ${ifProp(
      'isDisabled',
      css`
        h6,
        p {
          color: ${({ theme }) => theme.palette.text.light};
        }
      `
    )};

    &:hover {
      background: ${Colors.white};

      h6,
      p {
        color: ${({ theme }) => theme.palette.primary.main};
      }
    }

    span {
      font-size: 2rem;
    }

    p {
      margin: 0px;
    }
  }
`;

interface Props extends AnalyticsProps {
  type: string;
  icon?: string;
  title: string;
  description: string;
  isSelected: boolean;
  onAssetTypeSelect: (title: string) => void;
  height?: string;
}

const TooltipWrapper: FunctionComponent<{ isDisabled?: boolean }> = ({ children, isDisabled = false }) =>
  !isDisabled ? (
    <>{children}</>
  ) : (
    <Tooltip fadeEasing="linear" placement="top" content="Please contact support@inplayer.com for this type of asset.">
      {children}
    </Tooltip>
  );

const AssetTypeButton: FunctionComponent<Props> = ({
  type,
  icon = '',
  title,
  description,
  isSelected,
  onAssetTypeSelect,
  height,
  tag = '',
}) => {
  const isDisabled = type === AssetType.File || type === AssetType.RSS;

  return (
    <AssetTypeCard isSelected={isSelected} isDisabled={isDisabled} height={height || '96px'}>
      <TooltipWrapper isDisabled={isDisabled}>
        <Button tag={tag} onClick={() => onAssetTypeSelect(type)} icon={<CommonIcon name={icon} />} iconPosition="left">
          <BlueTypography variant="h6">{title}</BlueTypography>
          <Typography variant="p">{description}</Typography>
        </Button>
      </TooltipWrapper>
    </AssetTypeCard>
  );
};

export default AssetTypeButton;
